import * as wbd from "@khanacademy/wonder-blocks-data";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {khanFetch} from "@khan/data-fetch";
import {useGql, type GqlFetchFn} from "@khan/data-gql";
import {Errors, KAError, logUnreachableCode} from "@khan/logging";
import {InitialRequestUrl} from "@khan/shared-core";

import type {
    UpdateChallengeProgressData,
    UpdateChallengeProgressVariables,
    UpdateInteractiveProgressData,
    UpdateInteractiveProgressVariables,
    UpdateProjectProgressData,
    UpdateProjectProgressVariables,
    UpdateTalkthroughProgressData,
    UpdateTalkthroughProgressVariables,
} from "#content-library/queries.ts";
import type {UserProgram} from "#content-library/types/types.ts";
import type {CSContentKind} from "#tutorial-types-package/types.ts";

import type {
    programQuery,
    programQuery_programById_originScratchpad as programQueryOriginScratchpad,
    programQuery_programById_revision as programQueryRevision,
    programQuery_programById_topic as programQueryTopic,
} from "./graphql/__graphql-types__/programQuery.ts";
import {PROGRAM_QUERY} from "./graphql/program-query.ts";
import type * as sig from "./sig.types.ts";
import type {USER_SCRATCHPAD_SIG} from "./sig.types.ts";

type ProgramViewType =
    typeof import("#content-library/ui/program-view.tsx").default;

export type CSContentOptions = {
    discussion: sig.DISCUSSION_CONTEXT_SIG;
    embedded: boolean;
    creatorProfile: sig.USER_PROFILE_SIG;
    originScratchpad: sig.ORIGIN_SCRATCHPAD_SIG | null | undefined;
    flaggedByUser: boolean;
    upVoted: boolean;
    downVoted: boolean;
    userScratchpad: USER_SCRATCHPAD_SIG | UserProgram | null | undefined;
    updateChallengeProgress?: (
        arg1: UpdateChallengeProgressVariables,
    ) => Promise<Result<UpdateChallengeProgressData>>;
    updateInteractiveProgress?: (
        arg1: UpdateInteractiveProgressVariables,
    ) => Promise<Result<UpdateInteractiveProgressData>>;
    updateProjectProgress?: (
        arg1: UpdateProjectProgressVariables,
    ) => Promise<Result<UpdateProjectProgressData>>;
    updateTalkthroughProgress?: (
        arg1: UpdateTalkthroughProgressVariables,
    ) => Promise<Result<UpdateTalkthroughProgressData>>;
    // The types below are used for 321 CS content rendering. Legacy pages
    // do not provide these.

    // A custom component to render instead of the default
    // ScratchpadView component.
    // TODO(LP-12592): Properly type what ScratchpadUI passes down to the
    // view component. We could re-type this prop to be a function
    // signature that takes the context that ScratchpadUI passes down.
    renderComponent?: ProgramViewType;
    onComplete?: () => unknown;
    onStart?: () => unknown;
};

export type ProgramOptions = {
    discussion: sig.USER_PROGRAM_DISCUSSION_CONTEXT_SIG;
    embedded: boolean;
    scratchpad: sig.USER_PROGRAM_SCRATCHPAD_SIG;
    creatorProfile: sig.CREATOR_PROFILE_SIG;
    originScratchpad: sig.USER_PROGRAM_ORIGIN_SCRATCHPAD_SIG | null | undefined;
    flaggedByUser: boolean;
    upVoted: boolean;
    topic: sig.USER_PROGRAM_TOPIC_SIG;
};

const fetchCSContent = async (
    scratchpadId: string,
    contentKind: CSContentKind,
): Promise<CSContentOptions> => {
    const kind = contentKind.toLocaleLowerCase();
    const result = await khanFetch(
        `/api/internal/show_${kind}?${kind}_id=${scratchpadId}&casing=camel`,
    );
    if (result.ok) {
        return result.json();
    }
    throw new Error("Failed to retrieve scratchpad data");
};

const assertNotNull = <T>(
    nullable: T | null | undefined,
    errorMessage: string,
): T => {
    if (nullable == null) {
        throw new KAError(errorMessage, Errors.Internal);
    }

    return nullable;
};

const mapGQLToTopic = (
    raw?: programQueryTopic | null,
): ProgramOptions["topic"] => {
    if (raw == null) {
        return {id: "", slug: ""};
    }

    const {__typename: _, ...rest} = raw;
    return rest;
};

const mapGQLToOrigin = (
    raw?: programQueryOriginScratchpad | null,
): ProgramOptions["originScratchpad"] => {
    if (raw == null) {
        return null;
    }

    const {__typename: _, ...rest} = raw;
    return rest;
};

const mapGQLToRevision = (
    raw: programQueryRevision,
): ProgramOptions["scratchpad"]["revision"] => {
    const {__typename: _, id, ...rest} = raw;

    return {
        ...rest,
        id: +id,
    };
};

const mapUserAuthoredContentType = (
    type: "PJS" | "SQL" | "WEBPAGE" | "PYTHON",
): "pjs" | "sql" | "webpage" => {
    switch (type) {
        case "PJS":
            return "pjs";
        case "SQL":
            return "sql";
        case "WEBPAGE":
            return "webpage";
        case "PYTHON":
            throw new KAError(
                "Python programs are not supported in scratchpad UI",
                Errors.Internal,
            );
        default:
            throw logUnreachableCode(
                "Unrecognized userAuthoredContentType",
                type,
            );
    }
};

export const mapGQLToScratchpadOptions = (
    response: programQuery,
): ProgramOptions => {
    const program = response.programById;

    if (program == null) {
        throw new KAError("Program not found", Errors.NotFound);
    }

    if (program.revision == null) {
        throw new KAError("Revision can't be null", Errors.Internal);
    }
    const revision = program.revision;

    if (program.creatorProfile == null) {
        throw new KAError("Creator Profile can't be null", Errors.Internal);
    }

    const {nickname, profileRoot, profile} = program.creatorProfile;

    return {
        creatorProfile: {
            nickname: nickname,
            profileRoot: profileRoot,
            isPublic:
                assertNotNull(profile, "Profile not found").accessLevel ===
                "PUBLIC",
        },
        discussion: {
            canEdit: false,
            focusKind: "scratchpad",

            focusId: program.id,
            isOwner: program.isOwner,
            restrictPosting: program.restrictPosting,
            showProjectFeedback: !(program.byChild && program.isOwner),
        },
        embedded: false,
        flaggedByUser: program.flaggedByUser,
        originScratchpad: mapGQLToOrigin(program.originScratchpad),
        scratchpad: {
            contentKind: "Scratchpad",
            kind: "Scratchpad",
            type: "scratchpad",
            date: revision.created,
            flags: program.flags ?? [],

            byChild: program.byChild,
            category: program.category?.toLowerCase() ?? null,
            created: assertNotNull(program.created, "created not found"),
            deleted: program.deleted,
            description: program.description,
            docsUrlPath: program.docsUrlPath,
            flaggedBy: program.flaggedBy ?? [],
            height: program.height,
            hideFromHotlist: program.hideFromHotlist,
            id: +assertNotNull(program.id, "id not found"), // convert string to int, possibly unsafe
            imagePath: program.imagePath,
            isProjectOrFork: program.isProjectOrFork,
            originIsProject: program.isProjectOrFork,
            kaid: program.kaid,
            key: program.key,
            newUrlPath: program.newUrlPath,
            relativeUrl: assertNotNull(program.url, "relative url not found"),
            revision: mapGQLToRevision(revision),
            slug: assertNotNull(program.slug, "slug not found"),
            spinoffCount: assertNotNull(
                program.spinoffCount,
                "spinoffCount not found",
            ),
            sumVotesIncremented: assertNotNull(
                program.sumVotesIncremented,
                "sumVotesIncremented not found",
            ),
            title: program.title,
            translatedTitle: program.translatedTitle,
            translatedDescription: program.description,
            url:
                InitialRequestUrl.origin +
                assertNotNull(program.url, "URL not found"),
            userAuthoredContentType: mapUserAuthoredContentType(
                program.userAuthoredContentType,
            ),
            width: program.width,
        },
        topic: mapGQLToTopic(program.topic),
        upVoted: program.upVoted || false,
    };
};

const fetchProgramGQL = (
    gqlFetch: GqlFetchFn,
    programId: string,
): Promise<ProgramOptions> => {
    return gqlFetch(PROGRAM_QUERY, {variables: {programId}}).then(
        mapGQLToScratchpadOptions,
    );
};

export const useProgram = (programId: string): wbd.Result<ProgramOptions> => {
    const gqlFetch = useGql();
    const [result] = wbd.useCachedEffect(
        programId,
        () => fetchProgramGQL(gqlFetch, programId),
        {fetchPolicy: wbd.FetchPolicy.NetworkOnly},
    );
    return result;
};

export const useCSContent = (
    scratchpadId: string,
    contentKind: CSContentKind,
): wbd.Result<CSContentOptions> => {
    const [result] = wbd.useCachedEffect(
        scratchpadId,
        () => fetchCSContent(scratchpadId, contentKind),
        {fetchPolicy: wbd.FetchPolicy.NetworkOnly},
    );
    return result;
};
